import { differenceInCalendarDays, parseISO } from 'date-fns'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState, useRef } from 'react'
import styled, { css } from 'styled-components'
import { IcArrowRight } from '@healingpaper/cell-icons'

import { BannerImageData, getBannerImages } from './data/banners'

import Text from '@/components/atomic/atoms/Text'
import ImageBanner, { BannerImage } from '@/components/atomic/molecules/ImageBanner'
import SearchInput from '@/components/atomic/molecules/SearchInput'
import Section from '@/components/atomic/molecules/Section'
import MedicalTipWidget, { SimpleMedicalTip } from '@/components/atomic/organisms/MedicalTipWidget'
import SignUpPopup from '@/components/atomic/organisms/SignUpPopup'
import { getDownloadLink } from '@/core/lib'
import { useModal } from '@/hooks/Modal/Modal.hook'
import { useTranslation } from '@/lang'
import { generateTagId } from '@/utils/test-automation'
import { Locales } from '@/utils/language'
import { useLocales } from '@/shared/hooks/useLocales'
import { TreatmentCategory } from '@/features/treatment-category-filter/types/treatment-filter.type'
import { User } from '@/features/member/member.type'

interface Props {
  user: User | null
  treatments: TreatmentCategory[]
  medicalTips: SimpleMedicalTip[]
  onClickMoreColumn: () => void
  onClickColumn: (id: string) => void
  onClickMoreMedicalTip: () => void
  onClickMedicalTip: (id: number) => void
  onClickSearchInput: () => void
  onClickBanner: (tag?: string) => void
  onClickTreatment: (treatment: TreatmentCategory) => void
}

const HomeTemplate = ({
  treatments = [],
  medicalTips = [],
  onClickMoreMedicalTip,
  onClickMedicalTip,
  onClickSearchInput,
  onClickBanner,
  onClickTreatment,
}: Props): JSX.Element => {
  const router = useRouter()
  const { t } = useTranslation()
  const { locale, country } = useLocales()
  const { open, close } = useModal()
  const hasOpenedPopup = useRef(false)
  const isShownMedicalTip = locale === Locales.KO_KR && medicalTips.length >= 2
  const bannerImages: BannerImage<BannerImageData>[] = getBannerImages(locale)

  const filterBannerImage = (images: BannerImage<BannerImageData>[]) => {
    const today = new Date()

    return images.filter((img) => {
      if (img.data?.endDate && differenceInCalendarDays(parseISO(img.data.endDate), today) < 0) {
        return false
      }
      if (img.data?.startDate && differenceInCalendarDays(parseISO(img.data.startDate), today) > 0) {
        return false
      }
      return true
    })
  }

  const handleClickBanner = async (image: BannerImage<BannerImageData>) => {
    const url = image.data?.url || getDownloadLink()

    if (image.data?.isOpenInNewWindow) {
      window.open(url)
    } else {
      router.push(url)
    }

    onClickBanner(image.data?.tag)
  }

  const { isLocale } = useLocales()
  useEffect(() => {
    if (hasOpenedPopup.current) return

    const isGnunSignup = sessionStorage.getItem('gnun_signup')
    if (isLocale.JP || isLocale.KR) {
      if (isGnunSignup !== 'true') return
      hasOpenedPopup.current = true
      open(
        <SignUpPopup
          country={country}
          text={{ start: t('pages.homePage.SignUpPopup.start') }}
          onClose={() => {
            sessionStorage.removeItem('gnun_signup')
            close()
          }}
        />,
      )
    }
  }, [])

  return (
    <StyledContainer>
      <SearchInput placeholder={t('searchPopup.inputPlaceholder')} disabled onClick={onClickSearchInput} />

      <BannerSection padding={0} hasDivider={false}>
        <ImageBanner images={filterBannerImage(bannerImages)} onClick={handleClickBanner} />
      </BannerSection>

      <TitleSection padding={0} hasDivider={false}>
        <Text type="title2" element="h1" id={generateTagId('home', 'page', 'text', 'title', 'category')}>
          {t('main.title')}
        </Text>
      </TitleSection>

      <TreatmentSection padding={0} hasDivider={false}>
        <nav>
          <TreatmentList>
            {treatments.map((treatment, index) => {
              return (
                <li key={index}>
                  <div
                    role="menuitem"
                    tabIndex={-1}
                    onClick={() => onClickTreatment(treatment)}
                    onKeyDown={() => {
                      undefined // jsx-a11y/click-events-have-key-events 에러 회피용
                    }}
                  >
                    <Link href={`/search?category=${treatment.name}`} prefetch={false}>
                      {' '}
                    </Link>
                    <DelayedImage src={treatment.iconImageUrl} alt={treatment.name} width={40} />
                    <p className="typo-caption2 text-center break-words">{treatment.name}</p>
                  </div>
                </li>
              )
            })}
          </TreatmentList>
        </nav>
      </TreatmentSection>

      {isShownMedicalTip && (
        <StyledMedicalTipWidgetWrapper>
          <MedicalTipWidget
            medicalTips={medicalTips}
            onClickMedicalTip={onClickMedicalTip}
            onClickMore={onClickMoreMedicalTip}
          />
        </StyledMedicalTipWidgetWrapper>
      )}
    </StyledContainer>
  )
}

const MoreButton = ({ title, onClick }: { title: string; onClick: () => void }) => (
  <StyledMoreButton onClick={onClick}>
    {title}
    <IcArrowRight width={16} height={16} />
  </StyledMoreButton>
)

const BannerSection = styled(Section)`
  padding: 16px 26px 32px;
`

const TitleSection = styled(Section)`
  padding: 0px 26px 16px !important;
`

const TreatmentSection = styled(Section)`
  padding-bottom: 32px;
`

const TreatmentList = styled.ol`
  list-style: none;
  padding: 0px;
  margin: 0px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 8px;
  padding: 0 26px;
  box-sizing: border-box;
  place-items: center;
  li {
    display: inline-block;
    width: 100%;
    div {
      padding: 10px 0;
      position: relative;
      margin: 0 auto;
      z-index: 1;
      display: flex;
      border-radius: 8px;
      width: 100%;
      max-width: 97px;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      background: #ffffff;
      cursor: pointer;
      &:hover {
        box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.16);
      }
      a {
        display: block;
        position: absolute;
        width: 100%;
        height: 100%;
      }
    }
  }
`

const DelayedImage = ({ src, alt, width }: { src: string; alt: string; width: number }) => {
  const [imageSrc, setImageSrc] = useState('')

  useEffect(() => {
    setImageSrc(src)
  }, [src])

  return <StyledImage src={imageSrc} alt={alt} width={width} height={width} $visible={!!imageSrc} />
}

const StyledImage = styled.img<{ $visible: boolean }>`
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  margin-bottom: 10px;
`
const StyledMoreButton = styled.button`
  border: none;
  background: none;
  display: flex;
  align-items: center;
  padding: 0 0 0 10px;
  cursor: pointer;
  ${({ theme }) => css`
    color: ${theme.colors.gray800};
    ${theme.typography.description2}
  `}
`
const StyledContainer = styled.div`
  padding-bottom: 50px;
`

const StyledMedicalTipWidgetWrapper = styled.div`
  padding: 26px;
`

export default HomeTemplate
